'use client'
// Bouton de thème de la barre du haut : Clair · Sombre · Système (voir src/lib/app-theme.ts). S'applique à toute l'application.

import { useCallback, useEffect, useRef, useState } from 'react'
import { THEME_ATTR, THEME_EVENT, THEME_ICONS, THEME_KEY, THEME_LABELS, choiceFrom, resolveTheme } from '@/lib/app-theme'
import { THEME_CHOICES, type ThemeChoice } from '@/lib/dashboard-layout'

const read = (): ThemeChoice => { try { return choiceFrom(window.localStorage.getItem(THEME_KEY)) } catch { return 'dark' } }

/** Thème de l'application : choix + thème affiché. Plusieurs composants (et onglets) restent synchronisés. */
export function useAppTheme() {
  const [choice, setChoice] = useState<ThemeChoice>('dark')
  const [systemDark, setSystemDark] = useState(true)

  useEffect(() => {
    setChoice(read())
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    setSystemDark(mq.matches)
    const onMq = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    const sync = () => setChoice(read())
    mq.addEventListener('change', onMq); window.addEventListener(THEME_EVENT, sync); window.addEventListener('storage', sync)
    return () => { mq.removeEventListener('change', onMq); window.removeEventListener(THEME_EVENT, sync); window.removeEventListener('storage', sync) }
  }, [])

  const resolved = resolveTheme(choice, systemDark)
  useEffect(() => { document.documentElement.setAttribute(THEME_ATTR, resolved) }, [resolved])

  const set = useCallback((c: ThemeChoice) => {
    try { window.localStorage.setItem(THEME_KEY, c) } catch { /* stockage indisponible : le choix vaut pour cette page seulement */ }
    setChoice(c); window.dispatchEvent(new Event(THEME_EVENT))
  }, [])
  return { choice, resolved, set }
}

export function ThemeToggle() {
  const { choice, set } = useAppTheme()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', away); document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc) }
  }, [open])

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button onClick={() => setOpen(o => !o)} aria-label={`Thème : ${THEME_LABELS[choice]}`} aria-haspopup="menu" aria-expanded={open} title={`Thème : ${THEME_LABELS[choice]}`}
        style={{ width: 30, height: 30, borderRadius: 7, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {THEME_ICONS[choice]}
      </button>
      {open && (
        <div role="menu" aria-label="Thème" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: 130, zIndex: 999, background: '#0b0f1a', border: '1px solid rgba(79,124,255,0.2)', borderRadius: 10, boxShadow: '0 16px 40px rgba(0,0,0,0.6)', padding: 4 }}>
          {THEME_CHOICES.map(t => (
            <button key={t} role="menuitemradio" aria-checked={t === choice} onClick={() => { set(t); setOpen(false) }}
              style={{ width: '100%', textAlign: 'left', padding: '7px 10px', background: t === choice ? 'rgba(79,124,255,0.14)' : 'none', border: 'none', borderRadius: 7, color: t === choice ? '#fff' : 'rgba(255,255,255,0.65)', fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer', display: 'flex', gap: 8 }}>
              <span style={{ width: 14 }}>{THEME_ICONS[t]}</span>{THEME_LABELS[t]}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
